import React, {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Button, Divider, Select } from 'antd';
import { getLocalStorageValue, setLocalStorageValue } from '@/utils/helpers';
import { LocalStorageKeys } from '@/utils/constants';
import { useAppCustomers, useHttpAuthReady, useIdTokenClaims } from '@/hooks';
import { useUserPermissions } from '@/api/Users';

export const TenantSwitcher: FC = () => {
  const { hasLoggedIn } = useIdTokenClaims();
  const isHttpAuthReady = useHttpAuthReady();
  const {
    customerIds,
    setCustomerIds,
    locationIds,
    setLocationIds,
    userPermissions,
    setUserPermissions,
  } = useAppCustomers();

  const { data: userPermissionsData } = useUserPermissions(
    hasLoggedIn && isHttpAuthReady && !userPermissions.loaded,
  );
  const [loaded, setLoaded] = useState(false);
  const [selectedCustomers, setSelectedCustomers] = useState<number[]>([]);
  const [selectedLocations, setSelectedLocations] = useState<number[]>([]);

  useEffect(() => {
    if (!hasLoggedIn || userPermissions.loaded) return;
    if (!userPermissionsData) return;

    setUserPermissions(userPermissionsData);
  }, [
    hasLoggedIn,
    setUserPermissions,
    userPermissionsData,
    userPermissions.loaded,
  ]);

  const showApply = useMemo(
    () =>
      selectedCustomers.length > 0 &&
      selectedLocations.length > 0 &&
      (selectedCustomers.sort().join(',') !== customerIds.sort().join(',') ||
        selectedLocations.sort().join(',') !== locationIds.sort().join(',')),
    [customerIds, locationIds, selectedCustomers, selectedLocations],
  );

  const allCustomers = useMemo(
    () => userPermissions?.customers || [],
    [userPermissions],
  );

  const allCustomerIds = useMemo(
    () => allCustomers.map((o) => o.customerId),
    [allCustomers],
  );

  const allLocations = useMemo(
    () => userPermissions?.locations || [],
    [userPermissions],
  );

  const allLocationIds = useMemo(
    () => allLocations.map((o) => o.locationId),
    [allLocations],
  );

  const reloadPage = () => {
    window.location.reload();
  };

  const changeCustomerIds = useCallback(
    (key: number[]) => {
      if (key.length === 0 && allCustomerIds.length > 0) {
        key = [allCustomerIds[0]];
      }
      setLocalStorageValue(LocalStorageKeys.CustomerIds, key.join(','));
    },
    [allCustomerIds],
  );

  const changeLocationIds = useCallback(
    (key: number[]) => {
      if (key.length === 0 && allLocationIds.length > 0) {
        key = [allLocationIds[0]];
      }
      setLocalStorageValue(LocalStorageKeys.LocationIds, key.join(','));
    },
    [allLocationIds],
  );

  useEffect(() => {
    if (loaded || !userPermissions.loaded) return;

    const storagedCustomers = getLocalStorageValue(
      LocalStorageKeys.CustomerIds,
      '',
    )
      .split(',')
      .map((o) => +o)
      .filter((o) => o && allCustomerIds.includes(o));

    let shouldReload = false;
    if (storagedCustomers.length) {
      setSelectedCustomers(storagedCustomers);
      setCustomerIds(storagedCustomers);
    } else {
      shouldReload = allCustomerIds.length > 0;
      changeCustomerIds(allCustomerIds);
    }

    const storagedLocations = getLocalStorageValue(
      LocalStorageKeys.LocationIds,
      '',
    )
      .split(',')
      .map((o) => +o)
      .filter((o) => o && allLocationIds.includes(o));

    if (storagedLocations.length) {
      setSelectedLocations(storagedLocations);
      setLocationIds(storagedLocations);
    } else {
      shouldReload = shouldReload || allLocationIds.length > 0;
      changeLocationIds(allLocationIds);
    }
    setLoaded(true);
    if (shouldReload) reloadPage();
  }, [
    allCustomerIds,
    allLocationIds,
    changeCustomerIds,
    changeLocationIds,
    loaded,
    setCustomerIds,
    setLocationIds,
    userPermissions.loaded,
  ]);

  const tagRender = (label: ReactNode, isMaxTag: boolean) =>
    isMaxTag ? (
      <span className="ant-select-selection-item">
        <span className="ant-select-selection-item-content">{label}</span>
      </span>
    ) : (
      <span />
    );

  const maxTagPlaceholder = (omittedValues: any[]) =>
    omittedValues.length === 0
      ? ''
      : omittedValues.length > 1
        ? `${omittedValues.length} Selected`
        : omittedValues[0].label;

  const selectAllRender = (
    menu: React.ReactNode,
    onClear: () => void,
    onSelectAll: () => void,
  ) => (
    <>
      {menu}
      <Divider style={{ margin: '8px 0' }} />
      <div className="d-flex justify-content-between">
        <Button size="small" onClick={onClear}>
          Clear
        </Button>
        <Button size="small" onClick={onSelectAll}>
          Select All
        </Button>
      </div>
    </>
  );

  return userPermissions.loaded ? (
    <>
      <Select
        className="dark-select tenant-switcher-select"
        mode="multiple"
        maxTagCount={0}
        options={allLocations.map((o) => ({
          label: o.locationName,
          value: o.locationId,
        }))}
        style={{ width: 160, height: 40 }}
        value={selectedLocations}
        onChange={(key) => setSelectedLocations(key)}
        popupRender={(menu) =>
          selectAllRender(
            menu,
            () => setSelectedLocations([]),
            () => setSelectedLocations(allLocationIds),
          )
        }
        tagRender={({ label, isMaxTag }) => tagRender(label, isMaxTag)}
        maxTagPlaceholder={maxTagPlaceholder}
      />
      <Select
        className="dark-select tenant-switcher-select"
        mode="multiple"
        maxTagCount={0}
        options={allCustomers.map((o) => ({
          label: o.customerName,
          value: o.customerId,
        }))}
        style={{ width: 160, height: 40 }}
        value={selectedCustomers}
        onChange={(key) => setSelectedCustomers(key)}
        popupRender={(menu) =>
          selectAllRender(
            menu,
            () => setSelectedCustomers([]),
            () => setSelectedCustomers(allCustomerIds),
          )
        }
        tagRender={({ label, isMaxTag }) => tagRender(label, isMaxTag)}
        maxTagPlaceholder={maxTagPlaceholder}
      />
      <Button
        type="primary"
        disabled={!showApply}
        onClick={() => {
          changeCustomerIds(selectedCustomers);
          changeLocationIds(selectedLocations);
          reloadPage();
        }}
      >
        Apply Changes
      </Button>
    </>
  ) : null;
};
